/**
 * versioningSystem.js
 *
 * Automatic and manual document versioning.
 *
 * Rules (constants live in env.js → VERSIONING_CONFIG):
 *   1. Substantial edit  – 300+ cumulative chars → create auto version
 *   2. Inactivity        – 10 min no typing + any pending change → create auto version
 *   3. Minimum gap       – never two auto versions within 30 min
 *   4. Daily cap (8)     – when cap reached, rolling-merge the closest adjacent
 *                          auto-version pair, then create the new one
 *   5. End-of-day        – backend cron at midnight thins auto versions to 3
 *
 * Manual versions (created by the user) are NEVER subject to caps, merges, or thinning.
 */

import {
  VERSIONING_CONFIG,
  documentBackupBucketName,
  documentBackupTableName,
  supabaseServiceKey,
  loadConfig,
} from "../components/env";
import { getSupabaseAdmin } from "../components/supabaseClient";
import { supabaseSchema } from "../components/supabaseClient";
import {
  checkFrontendStorageJsonBlobSize,
  preflightRestrictedStorageJsonSize,
} from "../utils/storageJsonFrontendLimit";

const {
  SUBSTANTIAL_CHANGE_CHARS,
  INACTIVITY_TIMEOUT_MS,
  MIN_GAP_BETWEEN_VERSIONS_MS,
  DAILY_AUTO_VERSION_CAP,
} = VERSIONING_CONFIG;

const VERSION_DISPLAY_LOOKBACK = 5;
const STALE_CLOSEST_INDEX = 3;

// Marker prefix stamped onto `version_name` when a clinician signs a note.
// Signed-ness lives here rather than in `backup_type` because that column has
// a CHECK constraint (auto | manual | recovery) replicated across every tenant
// schema — widening it would need a fan-out migration. `version_name` is plain
// TEXT, so the marker needs no schema change at all.
//
// The version row it lands on already carries user_id / user_email / user_name
// / backup_timestamp plus a full content snapshot — that IS the signature.
// Signed versions are created as `manual`, which the prune path never touches
// (it only ever receives backup_type === 'auto'), so they're durable.
//
// Pure ASCII and free of LIKE metacharacters (% and _) so it can be matched
// with a plain `.like(prefix + '%')`.
export const SIGNED_VERSION_PREFIX = "[SIGNED] ";

// Marker prefix for the per-editor snapshot written when someone finishes an
// edit session on an already-signed note. Like SIGNED, these are `manual`
// versions (never pruned), so the post-sign audit trail is durable.
export const POST_SIGN_EDIT_PREFIX = "[POST-SIGN EDIT] ";

// ─── Utilities ────────────────────────────────────────────────────────────────

function wordJaccardSimilarity(a, b) {
  if (a == null || b == null) return 0;
  if (a === b) return 1;
  const wa = new Set(String(a).toLowerCase().split(/\s+/).filter(Boolean));
  const wb = new Set(String(b).toLowerCase().split(/\s+/).filter(Boolean));
  if (wa.size === 0 && wb.size === 0) return 1;
  let inter = 0;
  for (const w of wa) {
    if (wb.has(w)) inter++;
  }
  const union = wa.size + wb.size - inter;
  return union ? inter / union : 0;
}

/** Mirror of the Python _generate_backup_path(). */
function generateBackupPath(filePath, tsVersion, schema = null) {
  const parts = filePath.split("/");
  const filename = parts[parts.length - 1];

  let year = null;
  let scheduledDate = null;
  for (const part of parts) {
    const m = part.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      scheduledDate = part;
      year = m[1];
      break;
    }
  }
  if (!scheduledDate) {
    const now = new Date();
    year = String(now.getFullYear());
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    scheduledDate = `${year}-${mm}-${dd}`;
  }

  const nm = filename.match(/^(.+)-(\d+)\.json$/);
  let basePath;
  if (nm) {
    basePath = `${year}/${scheduledDate}/${nm[1]}-${nm[2]}`;
  } else {
    basePath = `${year}/${scheduledDate}/unknown`;
  }

  // Prefer the doc's OWN schema (the PATIENT's, for a cross-schema chart); fall
  // back to the viewer's active schema. Keeps a foreign patient's version blobs
  // filed under THEIR tenant path — matching where the DB rows now write.
  const effSchema = schema || supabaseSchema;
  const useSchema = effSchema && effSchema !== "public" ? effSchema : null;
  const schemaPrefix = useSchema ? `${useSchema}/` : "";
  return `${schemaPrefix}${basePath}/v${tsVersion}.json`;
}

/** Count insertions + deletions in a Quill delta. */
function countCharsChanged(delta) {
  if (!delta || !delta.ops) return 0;
  let insertCount = 0;
  let deleteCount = 0;
  for (const op of delta.ops) {
    if (typeof op.insert === "string") {
      insertCount += op.insert.length;
    } else if (op.insert != null) {
      insertCount += 1; // embedded object (image, etc.)
    } else if (typeof op.delete === "number") {
      deleteCount += op.delete;
    } else if (typeof op.retain === "number" && op.attributes) {
      insertCount += 1; // formatting change
    }
  }
  // If the delta has both inserts and deletes (paste-over-selection), count only
  // the inserts — the delete is the same chars being replaced, not new change.
  // If it's a pure delete (ctrl+x, backspace), count the deleted chars.
  return insertCount > 0 ? insertCount : deleteCount;
}

// ─── VersioningSystem class ───────────────────────────────────────────────────

class VersioningSystem {
  constructor(filePath, editorRef, schema = null) {
    this.filePath = filePath;
    this.editorRef = editorRef;
    // Postgres schema the version rows/blobs live in. For a cross-schema chart
    // this is the PATIENT's schema (not the viewer's home) so a foreign
    // patient's history reads+writes land in the right tenant. null → home
    // schema (EditableDocumentPage / same-schema charts) — unchanged behavior.
    this.schema = schema || null;
    this.editor = null;
    this.setupEditorInterval = null;
    this.isDestroyed = false;

    // Display state (read-only helpers)
    this.savedVersionCount = null;
    this.recentVersionPlainTexts = null;
    this.lastVersionText = null;
    this._versionDisplayListener = null;
    this._versionDisplayDebounceTimer = null;
    this.hasLoggedNoVersionHistory = false;

    // In-memory caches — eliminates repeated DB/storage round-trips
    // null = not yet loaded; array (possibly empty) = loaded
    this._versionsCache = null;
    // Maps backup_path → parsed JSON content (avoids re-downloading the same file)
    this._contentCache = new Map();

    // Auto-versioning state
    this.currentUser = null; // { id, name, email }
    this.cumulativeCharChanges = 0;
    this.lastAutoVersionTimestamp = null; // ms since epoch
    this.hasPendingChanges = false;
    this.inactivityTimer = null;
    this.isCreatingVersion = false; // creation lock
    // When true, auto-versioning is paused for this document. Set during a
    // post-sign edit session so the history gets exactly ONE manual snapshot
    // when the editor finishes, not a stream of auto versions.
    this.autoVersioningSuppressed = false;

    this.init();
  }

  // ─── Initialisation ──────────────────────────────────────────────────────

  init() {
    this.setupEditorInterval = setInterval(() => {
      if (this.isDestroyed) {
        clearInterval(this.setupEditorInterval);
        this.setupEditorInterval = null;
        return;
      }
      if (
        this.editorRef?.current?.getEditor &&
        typeof this.editorRef.current.getEditor === "function"
      ) {
        try {
          this.editor = this.editorRef.current.getEditor(0);
          if (this.editor) {
            clearInterval(this.setupEditorInterval);
            this.setupEditorInterval = null;
            this.setupEventListeners();
          }
        } catch {
          clearInterval(this.setupEditorInterval);
          this.setupEditorInterval = null;
        }
      }
    }, 500);
  }

  setupEventListeners() {
    if (!this.editor) return;
    try {
      // Always remove before adding — prevents double-registration when called
      // from both init()'s poll interval (~500ms) and the useEffect setTimeout (1000ms).
      this.editor.off("text-change", this.handleTextChange);
      this.editor.on("text-change", this.handleTextChange);
    } catch (e) {
      console.warn("VersioningSystem: failed to attach text-change listener", e);
    }
  }

  /** Load backup JSON using storage `info` preflight to avoid downloading oversized files. */
  async _fetchBackupJsonParsed(admin, backupPath) {
    try {
      const pf = await preflightRestrictedStorageJsonSize(
        admin,
        documentBackupBucketName,
        backupPath,
      );
      if (pf.status === "too_large") {
        console.warn(
          "[versioning] Skipping backup (preflight JSON limit):",
          backupPath,
        );
        return null;
      }
      if (pf.status === "not_found") return null;
      const { data, error } = await admin.storage
        .from(documentBackupBucketName)
        .download(backupPath);
      if (error || !data) return null;
      if (pf.status !== "ok") {
        const sc = checkFrontendStorageJsonBlobSize(data);
        if (!sc.ok) {
          console.warn(
            "[versioning] Skipping backup (exceeds frontend JSON limit):",
            backupPath,
            sc.message,
          );
          return null;
        }
      }
      const raw = await data.text();
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  // ─── User ─────────────────────────────────────────────────────────────────

  /** Fetch and store the current authenticated user's info. */
  // Route document_backups DB ops to the doc's schema (the PATIENT's schema for
  // a cross-schema chart, else home). Storage ops keep the RAW admin — the
  // backup bucket is global and the schema rides in the path (generateBackupPath).
  _scoped(admin) {
    return this.schema ? admin.schema(this.schema) : admin;
  }

  async setCurrentUser() {
    try {
      const { getSupabase } = await import("../components/supabaseClient");
      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      let name = user.email?.split("@")[0] || "";
      try {
        const admin = getSupabaseAdmin();
        const { data } = await admin
          .from("users")
          .select("name")
          .eq("id", user.id)
          .single();
        if (data?.name) name = data.name;
      } catch {
        /* use email fallback */
      }

      this.currentUser = { id: user.id, name, email: user.email || "" };
    } catch (e) {
      console.warn("VersioningSystem.setCurrentUser:", e);
    }
  }

  // ─── Text-change handler ──────────────────────────────────────────────────

  handleTextChange = (delta, _oldDelta, source) => {
    if (this.isDestroyed) return;

    // Only react to edits the local user made
    if (source === "user") {
      const chars = countCharsChanged(delta);
      const prevCount = this.cumulativeCharChanges;
      this.cumulativeCharChanges += chars;
      this.hasPendingChanges = true;

      // Collapsed debug log — expand in DevTools to inspect the delta ops
      const insertedText = (delta.ops || [])
        .filter((op) => typeof op.insert === "string")
        .map((op) => op.insert)
        .join("");
      const deletedCount = (delta.ops || [])
        .filter((op) => typeof op.delete === "number")
        .reduce((sum, op) => sum + op.delete, 0);
      console.groupCollapsed(
        `[Versioning] text-change: +${chars} chars counted | cumulative: ${prevCount} → ${this.cumulativeCharChanges} / ${SUBSTANTIAL_CHANGE_CHARS}` +
        (deletedCount > 0 ? ` (${deletedCount} deleted chars ignored)` : "")
      );
      console.log("ops:", delta.ops);
      if (insertedText) console.log("inserted text:", JSON.stringify(insertedText.slice(0, 200) + (insertedText.length > 200 ? "…" : "")));
      if (deletedCount > 0) console.log("deleted chars (not counted toward threshold):", deletedCount);
      console.groupEnd();

      this._resetInactivityTimer();

      // Only fire on the moment of crossing the threshold, not on every
      // subsequent keystroke above it — prevents a DB query per keypress.
      if (
        prevCount < SUBSTANTIAL_CHANGE_CHARS &&
        this.cumulativeCharChanges >= SUBSTANTIAL_CHANGE_CHARS
      ) {
        this._maybeCreateAutoVersion("SUBSTANTIAL EDIT — " + this.cumulativeCharChanges + "+ CHARACTERS CHANGED SINCE LAST VERSION");
      } else if (chars > 0 && this.cumulativeCharChanges < SUBSTANTIAL_CHANGE_CHARS) {
        const needed = SUBSTANTIAL_CHANGE_CHARS - this.cumulativeCharChanges;
        this._logVersionBlocked(
          insertedText ? `PASTE/INSERT OF ${chars} CHARS` : `DELETE OF ${chars} CHARS`,
          `RULE 1: BELOW THRESHOLD — ${this.cumulativeCharChanges}/${SUBSTANTIAL_CHANGE_CHARS} CHARS ACCUMULATED, NEED ${needed} MORE`
        );
      }
    }

    this.scheduleVersionDisplayNotify();
  };

  // ─── Inactivity timer ─────────────────────────────────────────────────────

  _resetInactivityTimer() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    this.inactivityTimer = setTimeout(() => {
      this.inactivityTimer = null;
      if (this.hasPendingChanges && !this.isDestroyed) {
        this._maybeCreateAutoVersion("INACTIVITY — 10 MINUTES WITHOUT TYPING WHILE UNSAVED CHANGES EXIST");
      }
    }, INACTIVITY_TIMEOUT_MS);
  }

  // ─── Auto-version gate ────────────────────────────────────────────────────

  async _maybeCreateAutoVersion(trigger = "AUTO") {
    if (this.isDestroyed) return;
    if (this.autoVersioningSuppressed) {
      this._logVersionBlocked(trigger, "AUTO-VERSIONING SUPPRESSED — POST-SIGN EDIT SESSION");
      return;
    }
    if (this.isCreatingVersion) {
      this._logVersionBlocked(trigger, "CONCURRENT CREATION ALREADY IN PROGRESS — SKIPPING DUPLICATE TRIGGER");
      return;
    }
    if (!this.hasPendingChanges) return;

    // Claim the lock BEFORE the first await so that a second concurrent call
    // (e.g. substantial-edit + inactivity firing simultaneously) is blocked
    // immediately rather than both slipping through the async gap.
    this.isCreatingVersion = true;
    try {
      // Rule 3: minimum gap — check the DB so the gap is enforced across all
      // users editing the same document, not just this browser instance.
      const lastAutoTs = await this._getLastAutoVersionTimestamp();
      const now = Date.now();
      if (lastAutoTs && now - lastAutoTs < MIN_GAP_BETWEEN_VERSIONS_MS) {
        const elapsedMin = Math.round((now - lastAutoTs) / 60000);
        const remainingMin = Math.round((MIN_GAP_BETWEEN_VERSIONS_MS - (now - lastAutoTs)) / 60000);
        this._logVersionBlocked(
          trigger,
          `RULE 3: ${Math.round(MIN_GAP_BETWEEN_VERSIONS_MS / 60000)}-MIN MINIMUM GAP — LAST VERSION WAS ${elapsedMin} MIN AGO, NEXT ALLOWED IN ~${remainingMin} MIN`
        );
        return;
      }

      // Rule 4: enforce daily cap with rolling merge
      const todayVersions = await this._getTodayAutoVersions();
      if (todayVersions.length >= DAILY_AUTO_VERSION_CAP) {
        this._logVersionBlocked(
          trigger,
          `RULE 4: DAILY CAP OF ${DAILY_AUTO_VERSION_CAP} REACHED — ROLLING MERGE IN PROGRESS TO FREE A SLOT`
        );
        await this._removeClosestAdjacentAutoPair(todayVersions);
      }

      await this._doCreateVersion("auto", null, trigger);
    } catch (e) {
      console.warn("VersioningSystem._maybeCreateAutoVersion:", e);
    } finally {
      this.isCreatingVersion = false;
    }
  }

  /** Print a yellow-bordered console banner when a version trigger was blocked by a rule. */
  _logVersionBlocked(trigger, reason) {
    const bar = "▒".repeat(64);
    const msg = [
      "",
      bar,
      `  🚫 VERSION TRIGGER BLOCKED`,
      `  TRIGGER: ${trigger.toUpperCase()}`,
      `  REASON: ${reason}`,
      bar,
      "",
    ].join("\n");
    console.log("%c" + msg, "color: #ffa000; font-weight: bold; font-size: 13px");
  }

  /**
   * Query the DB for the most recent auto version's timestamp.
   * Uses a short in-memory cache (30 s) to avoid a DB hit on every keystroke
   * while still reflecting versions created by other users promptly.
   */
  async _getLastAutoVersionTimestamp() {
    const CACHE_TTL_MS = 30_000;
    const now = Date.now();
    if (
      this._lastAutoTsCache !== undefined &&
      now - this._lastAutoTsCacheAt < CACHE_TTL_MS
    ) {
      return this._lastAutoTsCache;
    }
    try {
      const admin = getSupabaseAdmin();
      const { data } = await this._scoped(admin)
        .from(documentBackupTableName)
        .select("backup_timestamp")
        .eq("original_path", this.filePath)
        .eq("backup_type", "auto")
        .order("backup_timestamp", { ascending: false })
        .limit(1);
      const ts = data?.[0]?.backup_timestamp
        ? new Date(data[0].backup_timestamp).getTime()
        : null;
      this._lastAutoTsCache = ts;
      this._lastAutoTsCacheAt = now;
      return ts;
    } catch {
      return this.lastAutoVersionTimestamp ?? null;
    }
  }

  // ─── Daily cap helpers ────────────────────────────────────────────────────

  async _getTodayAutoVersions() {
    // Derive from in-memory cache when available — no DB hit needed.
    if (this._versionsCache !== null) {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      return this._versionsCache
        .filter(
          (v) =>
            v.backup_type === "auto" &&
            new Date(v.backup_timestamp) >= todayStart
        )
        .sort(
          (a, b) =>
            new Date(a.backup_timestamp) - new Date(b.backup_timestamp)
        );
    }
    // Fall back to DB query if cache hasn't been loaded yet.
    try {
      const admin = getSupabaseAdmin();
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const { data, error } = await this._scoped(admin)
        .from(documentBackupTableName)
        .select("id, backup_path, backup_timestamp")
        .eq("original_path", this.filePath)
        .eq("backup_type", "auto")
        .gte("backup_timestamp", todayStart.toISOString())
        .order("backup_timestamp", { ascending: true });

      if (error) return [];
      return data || [];
    } catch {
      return [];
    }
  }

  /**
   * Find the two adjacent auto versions whose content is most similar
   * (highest word-Jaccard similarity = least change between them), then
   * delete the older of the pair to free a slot.
   */
  async _removeClosestAdjacentAutoPair(versions) {
    if (versions.length < 2) return;

    const admin = getSupabaseAdmin();

    // Download plain text for each version (use content cache when available).
    const texts = await Promise.all(
      versions.map(async (v) => {
        try {
          let parsed = this._contentCache.get(v.backup_path);
          if (!parsed) {
            parsed = await this._fetchBackupJsonParsed(admin, v.backup_path);
            if (!parsed) return "";
            this._contentCache.set(v.backup_path, parsed);
          }
          const delta = parsed.delta || parsed.content;
          return delta ? this.getTextFromDelta(delta) : "";
        } catch {
          return "";
        }
      })
    );

    // Find adjacent pair with the highest similarity (most redundant pair)
    let maxSimilarity = -1;
    let mergeIdx = 0;
    for (let i = 0; i < versions.length - 1; i++) {
      const sim = wordJaccardSimilarity(texts[i], texts[i + 1]);
      if (sim > maxSimilarity) {
        maxSimilarity = sim;
        mergeIdx = i;
      }
    }

    // Delete the older version of the pair
    const toDelete = versions[mergeIdx];
    try {
      await admin.storage
        .from(documentBackupBucketName)
        .remove([toDelete.backup_path]);
    } catch (e) {
      console.warn("_removeClosestAdjacentAutoPair: storage delete failed", e);
    }
    try {
      await this._scoped(admin).from(documentBackupTableName).delete().eq("id", toDelete.id);
    } catch (e) {
      console.warn("_removeClosestAdjacentAutoPair: DB delete failed", e);
    }

    // Keep caches consistent after deletion.
    if (this._versionsCache !== null && toDelete.id != null) {
      this._versionsCache = this._versionsCache.filter(
        (v) => v.id !== toDelete.id
      );
    }
    this._contentCache.delete(toDelete.backup_path);
  }

  // ─── Core version creation ────────────────────────────────────────────────

  /** Print a large, all-caps, green-bordered console banner for version creation. */
  _logVersionCreated(type, trigger, versionNumber, versionName) {
    const bar = "█".repeat(64);
    const typeLabel = type === "manual" ? "MANUAL VERSION" : "AUTO VERSION";
    const numLabel = versionNumber ? ` (v${versionNumber})` : "";
    const nameLabel = versionName ? ` — "${versionName}"` : "";
    const msg = [
      "",
      bar,
      `  📄 ${typeLabel} CREATED${numLabel}${nameLabel}`,
      `  TRIGGER: ${trigger.toUpperCase()}`,
      `  DOCUMENT: ${this.filePath}`,
      bar,
      "",
    ].join("\n");
    console.log("%c" + msg, "color: #00c853; font-weight: bold; font-size: 13px");
  }

  /**
   * Create a version.
   * @param {"auto"|"manual"} type
   * @param {string|null} versionName  Custom name (manual) or null (auto).
   * @param {string} trigger  Human-readable reason shown in the console log.
   * @returns {number|null} Version number or null on failure.
   */
  async _doCreateVersion(type, versionName, trigger = "UNKNOWN", opts = {}) {
    // `opts.content` lets a caller snapshot an EXPLICIT delta instead of reading
    // the live editor (used by the exit-flush, where the editor is already
    // unmounting). `opts.ignoreDestroyed` lets that same flush run even after
    // destroy() — the DB/storage clients are module-level, so the write still
    // completes as the component tears down.
    const { content: providedContent = null, ignoreDestroyed = false } = opts;
    this.lastCreateError = null;
    if (this.isDestroyed && !ignoreDestroyed) return null;

    const content = providedContent || this.getCurrentContent();
    if (!content) {
      this.lastCreateError = { stage: "content", code: "", message: "editor content unavailable" };
      return null;
    }

    const text = this.getTextFromDelta(content);
    if (!text.trim()) return null;

    // No-diff guard: skip auto versions only. Manual saves are explicit user snapshots.
    if (type === "auto" && this.lastVersionText !== null && this.lastVersionText !== undefined && text === this.lastVersionText) {
      const bar = "▒".repeat(64);
      console.log(
        "%c" + [
          "",
          bar,
          `  ⏭️  VERSION SKIPPED — NO DIFF vs PREVIOUS VERSION`,
          `  TRIGGER: ${trigger.toUpperCase()}`,
          `  DOCUMENT: ${this.filePath}`,
          bar,
          "",
        ].join("\n"),
        "color: #ff9800; font-weight: bold; font-size: 13px"
      );
      return null;
    }

    if (!supabaseServiceKey) {
      try {
        await loadConfig();
      } catch {
        /* ignore */
      }
    }
    if (!supabaseServiceKey) return null;

    const admin = getSupabaseAdmin();
    const now = new Date();

    // Mirror the timestamp format used by appointment_version_worker.py:
    // "2025-11-12T160000000Z"
    const ts = now
      .toISOString()
      .replace(/-/g, "")
      .replace(/:/g, "")
      .replace(".", "")
      .replace("Z", "") + "Z";

    const backupPath = generateBackupPath(this.filePath, ts, this.schema);

    // Strip embedded base64 images before storing — they can be MB each and
    // the original document already holds them. Replace with a placeholder so
    // the diff/restore logic still sees the correct structure.
    const sanitizedOps = (content.ops || []).map((op) => {
      if (op.insert && typeof op.insert === "object" && op.insert.image) {
        const src = op.insert.image;
        if (typeof src === "string" && src.startsWith("data:")) {
          return { ...op, insert: { image: "[base64-image-stripped]" } };
        }
      }
      return op;
    });
    const sanitizedContent = { ops: sanitizedOps };

    const backupData = {
      delta: sanitizedContent,
      lastUpdated: now.toISOString(),
      versionType: type,
      reason: type === "manual" ? versionName || "Manual version" : "Auto version",
    };
    const backupJson = JSON.stringify(backupData);

    // Upload file
    const { error: uploadErr } = await admin.storage
      .from(documentBackupBucketName)
      .upload(backupPath, backupJson, {
        contentType: "application/json",
        upsert: false,
      });
    if (uploadErr) {
      this.lastCreateError = {
        stage: "storage",
        code: uploadErr.statusCode || uploadErr.name || "",
        message: uploadErr.message || String(uploadErr),
      };
      console.error(
        `VersioningSystem: storage upload failed — ${this.lastCreateError.code} ${this.lastCreateError.message} (bucket=${documentBackupBucketName}, path=${backupPath})`,
        uploadErr
      );
      return null;
    }

    // Determine version number from cache (avoids a SELECT id count query).
    const versionNumber =
      (this._versionsCache !== null
        ? this._versionsCache.length
        : this.savedVersionCount ?? 0) + 1;

    const autoName = `Version ${versionNumber}`;

    const insertPayload = {
      original_path: this.filePath,
      backup_path: backupPath,
      backup_timestamp: now.toISOString(),
      file_size: backupJson.length,
      user_id: this.currentUser?.id || null,
      backup_type: type,
      user_email: this.currentUser?.email || "",
      user_name: this.currentUser?.name || "",
      version_name: versionName || autoName,
    };

    const { data: insertedRow, error: insertErr } = await this._scoped(admin)
      .from(documentBackupTableName)
      .insert(insertPayload)
      .select()
      .single();

    if (insertErr) {
      this.lastCreateError = {
        stage: "db",
        code: insertErr.code || insertErr.name || "",
        message: insertErr.message || String(insertErr),
      };
      console.warn("VersioningSystem: DB insert failed", insertErr);
      // Clean up orphan file
      try {
        await admin.storage.from(documentBackupBucketName).remove([backupPath]);
      } catch {
        /* ignore */
      }
      return null;
    }

    // Store the uploaded content in the content cache so the modal can
    // preview/diff this version without a storage download.
    this._contentCache.set(backupPath, backupData);

    // Prepend the new row to the versions cache so subsequent modal opens
    // and internal helpers (count, daily cap) see it immediately.
    if (this._versionsCache !== null) {
      const newRow = insertedRow || { ...insertPayload };
      this._versionsCache = [newRow, ...this._versionsCache];
    }

    // Update in-memory display state
    this.savedVersionCount = (this.savedVersionCount || 0) + 1;
    this.lastVersionText = text;
    this.recentVersionPlainTexts = [
      text,
      ...(this.recentVersionPlainTexts || []),
    ].slice(0, VERSION_DISPLAY_LOOKBACK);

    if (type === "auto") {
      this.lastAutoVersionTimestamp = Date.now();
      // Immediately update the DB-backed gap cache so this user also respects
      // the 30-min gap without waiting for the cache TTL to expire.
      this._lastAutoTsCache = this.lastAutoVersionTimestamp;
      this._lastAutoTsCacheAt = this.lastAutoVersionTimestamp;
      this.cumulativeCharChanges = 0;
      this.hasPendingChanges = false;
    }

    this._logVersionCreated(type, trigger, versionNumber, versionName || autoName);
    this.notifyVersionDisplay();
    return versionNumber;
  }

  /**
   * Public API: create a manual version.
   * No rate limiting — manual versions are always allowed.
   * @param {string} [versionName]
   * @param {string} [trigger]  Reason shown in the console log.
   */
  async createVersion(versionName, trigger) {
    return this._doCreateVersion(
      "manual",
      versionName || null,
      trigger || "MANUALLY CREATED BY USER"
    );
  }

  /** Pause/resume auto-versioning for this document (post-sign edit sessions). */
  setAutoVersioningSuppressed(on) {
    this.autoVersioningSuppressed = !!on;
  }

  /**
   * Create a manual version from an EXPLICIT delta (not the live editor).
   * Used to snapshot a post-sign edit session when the document is exited, where
   * the editor is already gone. Runs even after destroy().
   */
  async createVersionFromDelta(delta, versionName, trigger) {
    return this._doCreateVersion(
      "manual",
      versionName || null,
      trigger || "POST-SIGN EDIT",
      { content: delta, ignoreDestroyed: true }
    );
  }

  /**
   * Count this document's versions whose version_name starts with `prefix`.
   * Used to number "Master Note Version X" saves. Reads through the same
   * schema-scoped client the versions are written with, so the count is
   * consistent across reload/logout. Returns 0 on any error.
   */
  async countVersionsByNamePrefix(prefix) {
    try {
      const admin = getSupabaseAdmin();
      const { count, error } = await this._scoped(admin)
        .from(documentBackupTableName)
        .select("id", { count: "exact", head: true })
        .eq("original_path", this.filePath)
        .like("version_name", `${prefix}%`);
      if (error) {
        console.warn("VersioningSystem.countVersionsByNamePrefix:", error.message);
        return 0;
      }
      return count || 0;
    } catch (e) {
      console.warn("VersioningSystem.countVersionsByNamePrefix:", e?.message || e);
      return 0;
    }
  }

  // ─── Cache API (used by VersionHistoryModal) ─────────────────────────────

  /** Return the cached array of all version DB rows, or null if not yet loaded. */
  getVersionsCache() {
    return this._versionsCache;
  }

  /**
   * Store an array of version rows in the cache.
   * Called by VersionHistoryModal after it queries the DB so subsequent
   * modal opens can skip the round-trip.
   */
  updateVersionsCache(versions) {
    this._versionsCache = Array.isArray(versions) ? versions : null;
  }

  /**
   * Invalidate all version caches (call after bulk operations such as
   * "Reduce old versions" so the next modal open re-fetches fresh data).
   */
  invalidateVersionsCache() {
    this._versionsCache = null;
    this._contentCache = new Map();
    // Also bust the last-auto-ts DB cache so the gap check re-reads.
    this._lastAutoTsCache = undefined;
    this._lastAutoTsCacheAt = undefined;
  }

  /** Return the cached parsed JSON for a backup file path, or undefined. */
  getCachedContent(backupPath) {
    return this._contentCache.get(backupPath);
  }

  /** Store parsed JSON content in the content cache. */
  setCachedContent(backupPath, parsedContent) {
    this._contentCache.set(backupPath, parsedContent);
  }

  // ─── Display state ────────────────────────────────────────────────────────

  setVersionDisplayListener(fn) {
    this._versionDisplayListener = typeof fn === "function" ? fn : null;
  }

  scheduleVersionDisplayNotify() {
    if (this._versionDisplayDebounceTimer) {
      clearTimeout(this._versionDisplayDebounceTimer);
    }
    this._versionDisplayDebounceTimer = setTimeout(() => {
      this._versionDisplayDebounceTimer = null;
      this.notifyVersionDisplay();
    }, 200);
  }

  notifyVersionDisplay() {
    if (this.isDestroyed || !this._versionDisplayListener) return;
    try {
      this._versionDisplayListener(this.getVersionDisplayState());
    } catch (e) {
      console.warn("notifyVersionDisplay:", e);
    }
  }

  getVersionDisplayState() {
    const n = this.savedVersionCount;
    const content = this.getCurrentContent();
    const text = content ? this.getTextFromDelta(content) : "";

    if (n === null || n === undefined) {
      return {
        savedNumber: n,
        isOutOfDate: false,
        isStaleVersion: false,
        label: null,
        loading: true,
      };
    }

    if (n === 0) {
      const baselineReady = this.lastVersionText !== null;
      const isOutOfDate = baselineReady && text !== this.lastVersionText;
      return {
        savedNumber: n,
        isOutOfDate,
        isStaleVersion: false,
        label: isOutOfDate ? "No versions · Out of date" : "No versions",
        loading: false,
      };
    }

    const recent = this.recentVersionPlainTexts;
    if (recent && recent.length > 0) {
      let bestIdx = 0;
      let bestScore = -1;
      for (let i = 0; i < recent.length; i++) {
        const s = wordJaccardSimilarity(text, recent[i]);
        if (s > bestScore) {
          bestScore = s;
          bestIdx = i;
        }
      }
      const isExactLatest = text === recent[0];
      const isStaleVersion = bestIdx >= STALE_CLOSEST_INDEX;
      const isCurrentWithEdits = bestIdx === 0 && !isExactLatest;

      let label;
      if (isStaleVersion) {
        label = `v${n} · Stale version`;
      } else if (isCurrentWithEdits) {
        label = `v${n} · edited`;
      } else if (bestIdx > 0 && bestIdx < STALE_CLOSEST_INDEX) {
        label = `v${n} · behind`;
      } else {
        label = `v${n}`;
      }

      return {
        savedNumber: n,
        isOutOfDate: !isExactLatest,
        isStaleVersion,
        label,
        loading: false,
      };
    }

    const baselineReady = this.lastVersionText !== null;
    const isOutOfDate = baselineReady && text !== this.lastVersionText;
    const isStaleVersion = Boolean(isOutOfDate && typeof n === "number" && n > 1);
    let label;
    if (isStaleVersion) {
      label = `v${n} · Stale version`;
    } else if (isOutOfDate) {
      label = `v${n} · Out of date`;
    } else {
      label = `v${n}`;
    }
    return {
      savedNumber: n,
      isOutOfDate,
      isStaleVersion,
      label,
      loading: false,
    };
  }

  // ─── DB helpers ───────────────────────────────────────────────────────────

  async refreshSavedVersionCount() {
    if (!this.filePath || this.isDestroyed) return;
    // Use the in-memory cache when available — no DB hit needed.
    if (this._versionsCache !== null) {
      this.savedVersionCount = this._versionsCache.length;
      return;
    }
    if (!supabaseServiceKey) {
      try {
        await loadConfig();
      } catch {
        /* ignore */
      }
    }
    if (!supabaseServiceKey) return;
    try {
      const admin = getSupabaseAdmin();
      const { data, error } = await this._scoped(admin)
        .from(documentBackupTableName)
        .select("id")
        .eq("original_path", this.filePath);
      if (error) {
        console.warn("refreshSavedVersionCount:", error);
        return;
      }
      this.savedVersionCount = (data || []).length;
    } catch (e) {
      console.warn("refreshSavedVersionCount:", e);
    }
  }

  async syncVersionBaselineFromServer() {
    if (this.isDestroyed || !this.filePath) return;
    if (!supabaseServiceKey) {
      try { await loadConfig(); } catch { /* ignore */ }
    }
    if (!supabaseServiceKey) return;

    const admin = getSupabaseAdmin();

    // Single SELECT * populates the versions cache used by the modal and all
    // internal helpers (count, daily cap, display texts) — replaces the two
    // separate queries (count SELECT + backup_path SELECT) that were here before.
    let versionRows;
    try {
      const { data: rows, error } = await this._scoped(admin)
        .from(documentBackupTableName)
        .select("*")
        .eq("original_path", this.filePath)
        .order("backup_timestamp", { ascending: false });

      if (error) {
        console.warn("syncVersionBaselineFromServer: fetch error", error.message);
        versionRows = Array.isArray(this._versionsCache) ? this._versionsCache : [];
      } else {
        versionRows = Array.isArray(rows) ? rows : [];
      }
    } catch (e) {
      console.warn("syncVersionBaselineFromServer:", e);
      versionRows = Array.isArray(this._versionsCache) ? this._versionsCache : [];
    }

    if (this.isDestroyed) return;

    this._versionsCache = versionRows;
    this.savedVersionCount = versionRows.length;

    if (this.savedVersionCount === 0) {
      this.recentVersionPlainTexts = null;
      const content = this.getCurrentContent();
      if (content) this.lastVersionText = this.getTextFromDelta(content);
      this.notifyVersionDisplay();
      return;
    }

    // Download content for the most recent VERSION_DISPLAY_LOOKBACK versions.
    // Checks _contentCache first so re-loads (e.g. hot-reload) don't re-download.
    // Use versionRows (snapshot): awaits below allow other tasks to null
    // this._versionsCache via invalidateVersionsCache / destroy.
    const topRows = versionRows.slice(0, VERSION_DISPLAY_LOOKBACK);
    const texts = [];
    for (const row of topRows) {
      try {
        let parsed = this._contentCache.get(row.backup_path);
        if (!parsed) {
          parsed = await this._fetchBackupJsonParsed(admin, row.backup_path);
          if (!parsed) continue;
          this._contentCache.set(row.backup_path, parsed);
        }
        const delta = parsed.delta || parsed.content;
        if (delta) texts.push(this.getTextFromDelta(delta));
      } catch {
        /* skip bad backup */
      }
    }

    if (texts.length > 0) {
      this.recentVersionPlainTexts = texts;
      this.lastVersionText = texts[0];
    } else {
      this.recentVersionPlainTexts = null;
      const content = this.getCurrentContent();
      if (content) this.lastVersionText = this.getTextFromDelta(content);
    }

    if (!this.isDestroyed) {
      this._versionsCache = versionRows;
    }
    this.notifyVersionDisplay();
  }

  // ─── Editor helpers ───────────────────────────────────────────────────────

  getCurrentContent() {
    if (this.isDestroyed || !this.editor || !this.editorRef?.current?.getEditor) {
      return null;
    }
    try {
      return this.editor.getContents();
    } catch {
      return null;
    }
  }

  getTextFromDelta(delta) {
    if (!delta || !delta.ops) return "";
    return delta.ops
      .filter((op) => op.insert && typeof op.insert === "string")
      .map((op) => op.insert)
      .join("");
  }

  // ─── Debug helpers ────────────────────────────────────────────────────────

  async showVersionHistory() {
    try {
      if (!supabaseServiceKey) return;
      const admin = getSupabaseAdmin();
      const { data: versions, error } = await this._scoped(admin)
        .from(documentBackupTableName)
        .select("*")
        .eq("original_path", this.filePath)
        .order("backup_timestamp", { ascending: false });

      if (error) {
        console.error("Error fetching version history:", error);
        return;
      }

      if (!versions || versions.length === 0) {
        if (!this.hasLoggedNoVersionHistory) {
          console.log("No version history found for this document");
          this.hasLoggedNoVersionHistory = true;
        }
        return;
      }
      if (this.hasLoggedNoVersionHistory && versions.length > 0) {
        this.hasLoggedNoVersionHistory = false;
      }

      console.table(
        versions.map((v, i) => ({
          "#": i + 1,
          Timestamp: new Date(v.backup_timestamp).toLocaleString(),
          User: v.user_name || "Unknown",
          Type: v.backup_type || "unknown",
          Name: v.version_name || "",
          "Backup Path": v.backup_path,
        }))
      );
      return { totalVersions: versions.length, versions, document: this.filePath };
    } catch (error) {
      console.error("showVersionHistory:", error);
    }
  }

  async showEntireVersionDatabase() {
    try {
      if (!supabaseServiceKey) {
        console.warn("Supabase config not yet loaded");
        return;
      }
      const admin = getSupabaseAdmin();
      const { data: versions, error } = await this._scoped(admin)
        .from(documentBackupTableName)
        .select("*")
        .order("backup_timestamp", { ascending: false });

      if (error) {
        console.error("showEntireVersionDatabase:", error);
        return;
      }
      console.log("Total versions:", versions?.length ?? 0);
      if (versions?.length) {
        console.table(
          versions.slice(0, 50).map((v) => ({
            path: v.original_path,
            when: new Date(v.backup_timestamp).toLocaleString(),
            type: v.backup_type,
          }))
        );
        if (versions.length > 50) console.log(`… and ${versions.length - 50} more`);
      }
      return { totalVersions: versions?.length ?? 0, versions };
    } catch (error) {
      console.error("showEntireVersionDatabase:", error);
    }
  }

  // ─── No-ops kept for backward-compat ─────────────────────────────────────

  trackCollaboratorEdit() {}
  trackCollaboratorPresence() {}
  updateCollaborators() {}
  ensureAllActiveUsersTracked() {}

  // ─── Cleanup ──────────────────────────────────────────────────────────────

  destroy() {
    this.isDestroyed = true;

    if (this._versionDisplayDebounceTimer) {
      clearTimeout(this._versionDisplayDebounceTimer);
      this._versionDisplayDebounceTimer = null;
    }
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (this.setupEditorInterval) {
      clearInterval(this.setupEditorInterval);
      this.setupEditorInterval = null;
    }
    if (this.editor) {
      try {
        this.editor.off("text-change", this.handleTextChange);
      } catch {
        /* editor may be gone */
      }
    }
    this._versionDisplayListener = null;
    this.recentVersionPlainTexts = null;
    this._versionsCache = null;
    this._contentCache = new Map();
    this.editor = null;
  }
}

// ─── Browser debug helpers ────────────────────────────────────────────────────

window.VersioningSystemDebug = {
  showHistory: (vs) => {
    if (vs?.showVersionHistory) {
      vs.showVersionHistory();
    } else {
      console.error("VersioningSystem instance not found");
    }
  },
  showEntireDatabase: (vs) => {
    if (vs?.showEntireVersionDatabase) {
      vs.showEntireVersionDatabase();
    } else {
      console.error("VersioningSystem instance not found");
    }
  },
};

export const createVersioningSystem = (filePath, editorRef, schema = null) =>
  new VersioningSystem(filePath, editorRef, schema);

export default VersioningSystem;
